import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Users, Target as TargetIcon, Gauge } from "lucide-react";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";
import { Side } from "@/lib/types/side";
import { Motion } from "@/lib/types/motion";
import type { Veterancy, Cohesion } from "@/lib/types/veterancy-cohesion";
import { ExperiencePackDescriptor } from "@/lib/types/veterancy-cohesion";
import {
  VETERANCIES,
  SF_VETERANCIES,
  ARTY_VETERANCIES,
  PLANE_VETERANCIES,
  HELICOPTER_VETERANCIES,
  COHESIONS,
  PLANE_COHESIONS,
  getVeterancyDisplayName,
} from "@/lib/types/veterancy-cohesion";
import { MovementType } from "@/lib/types/unit";
import { TERRAIN_NAMES_MAP } from "@/lib/utils/terrains";

interface DamageCalculatorControlsProps {
  weapon: WeaponData | undefined;
  sourceUnit: Unit | undefined;
  targetUnit: Unit | undefined;
  distance: number;
  minRange: number;
  maxRange: number;
  side: Side;
  terrain: string;
  motion: Motion;
  sourceVeterancy: Veterancy | undefined;
  targetVeterancy: Veterancy | undefined;
  cohesion: Cohesion | undefined;
  onDistanceChange: (distance: number) => void;
  onSideChange: (side: Side) => void;
  onTerrainChange: (terrain: string) => void;
  onMotionChange: (motion: Motion) => void;
  onSourceVeterancyChange: (veterancy: Veterancy | undefined) => void;
  onTargetVeterancyChange: (veterancy: Veterancy | undefined) => void;
  onCohesionChange: (cohesion: Cohesion | undefined) => void;
}

function getVeterancyOptions(unit: Unit | undefined): Veterancy[] {
  if (!unit) {
    return VETERANCIES;
  }

  const xpBonuses = unit.xpBonuses;

  if (xpBonuses.includes(ExperiencePackDescriptor.SF)) {
    return SF_VETERANCIES;
  } else if (xpBonuses.includes(ExperiencePackDescriptor.ARTILLERY)) {
    return ARTY_VETERANCIES;
  } else if (xpBonuses.includes(ExperiencePackDescriptor.PLANE)) {
    return PLANE_VETERANCIES;
  } else if (xpBonuses.includes(ExperiencePackDescriptor.HELICOPTER)) {
    return HELICOPTER_VETERANCIES;
  } else {
    return VETERANCIES;
  }
}

function getCohesionOptions(unit: Unit | undefined): Cohesion[] {
  if (!unit) {
    return COHESIONS;
  }

  if (unit.movementType === MovementType.PLANE) {
    return PLANE_COHESIONS;
  }

  return COHESIONS;
}

export function DamageCalculatorControls({
  weapon,
  sourceUnit,
  targetUnit,
  distance,
  minRange,
  maxRange,
  side,
  terrain,
  motion,
  sourceVeterancy,
  targetVeterancy,
  cohesion,
  onDistanceChange,
  onSideChange,
  onTerrainChange,
  onMotionChange,
  onSourceVeterancyChange,
  onTargetVeterancyChange,
  onCohesionChange,
}: DamageCalculatorControlsProps) {
  const shouldDisableOptions =
    !weapon || !targetUnit || maxRange <= 0;

  // Local state for input value to allow typing
  const [inputValue, setInputValue] = useState<string>(distance.toString());

  // Sync input value when distance prop changes (from slider or external)
  useEffect(() => {
    setInputValue(distance.toString());
  }, [distance]);

  const sourceVeterancyOptions = getVeterancyOptions(sourceUnit);
  const targetVeterancyOptions = getVeterancyOptions(targetUnit);
  const cohesionOptions = getCohesionOptions(sourceUnit);

  // Get available terrains for target unit
  const availableTerrains = targetUnit?.occupiableTerrains
    ? [
        "None",
        ...targetUnit.occupiableTerrains.map((t) => {
          const mapped = TERRAIN_NAMES_MAP[t as keyof typeof TERRAIN_NAMES_MAP];
          return mapped || t;
        }),
      ]
    : ["None"];

  // Handle input change - allow typing, update immediately if valid
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Update immediately if it's a valid number
    const numValue = Number(value);
    if (!isNaN(numValue) && value.trim() !== "") {
      // Clamp to min/max range and update
      const clampedValue = Math.max(minRange, Math.min(maxRange, numValue));
      onDistanceChange(clampedValue);
    }
  };

  const handleInputBlur = () => {
    const numValue = Number(inputValue);
    if (isNaN(numValue) || inputValue.trim() === "") {
      // Invalid or empty, reset to current distance
      setInputValue(distance.toString());
    } else {
      // Clamp to min/max range (in case user typed something outside range)
      const clampedValue = Math.max(minRange, Math.min(maxRange, numValue));
      setInputValue(clampedValue.toString());
      onDistanceChange(clampedValue);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  return (
    <div className="space-y-2">
      {shouldDisableOptions && targetUnit && (
        <div className="p-1.5 rounded-lg border-2 border-destructive/50 bg-destructive/5">
          <div className="text-xs text-destructive font-semibold text-center">
            Weapon cannot target unit
          </div>
        </div>
      )}

      {/* Source Unit Controls */}
      <div className="space-y-1.5 pb-2 border-b">
        <div className="text-xs font-semibold flex items-center gap-1 text-muted-foreground">
          <Users className="h-3 w-3" />
          Source
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
        <div className="space-y-0.5">
          <Label htmlFor="source-veterancy" className="text-[11px]">Veterancy</Label>
          <Select
            value={sourceVeterancy || "none"}
            onValueChange={(value) =>
              onSourceVeterancyChange(
                value === "none" ? undefined : (value as Veterancy)
              )
            }
            disabled={shouldDisableOptions}
          >
            <SelectTrigger id="source-veterancy" className="w-full h-7 text-xs py-0">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {sourceVeterancyOptions.map((vet) => (
                <SelectItem key={vet} value={vet}>
                  {getVeterancyDisplayName(vet)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-0.5">
          <Label htmlFor="source-cohesion" className="text-[11px]">Cohesion</Label>
          <Select
            value={cohesion || "none"}
            onValueChange={(value) =>
              onCohesionChange(value === "none" ? undefined : (value as Cohesion))
            }
            disabled={shouldDisableOptions}
          >
            <SelectTrigger id="source-cohesion" className="w-full h-7 text-xs py-0">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {cohesionOptions.map((coh) => (
                <SelectItem key={coh} value={coh}>
                  {coh}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-0.5">
          <Label htmlFor="source-motion" className="text-[11px]">Motion</Label>
          <Select
            value={motion}
            onValueChange={(value) => onMotionChange(value as Motion)}
            disabled={shouldDisableOptions}
          >
            <SelectTrigger id="source-motion" className="w-full h-7 text-xs py-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={Motion.STATIC}>Static</SelectItem>
              <SelectItem value={Motion.MOVING}>Moving</SelectItem>
            </SelectContent>
          </Select>
        </div>
        </div>
      </div>

      {/* Target Unit Controls */}
      <div className="space-y-1.5 pb-2 border-b">
        <div className="text-xs font-semibold flex items-center gap-1 text-muted-foreground">
          <TargetIcon className="h-3 w-3" />
          Target
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
            <div className="space-y-0.5">
              <Label htmlFor="target-veterancy" className="text-[11px]">Veterancy</Label>
          <Select
            value={targetVeterancy || "none"}
            onValueChange={(value) =>
              onTargetVeterancyChange(
                value === "none" ? undefined : (value as Veterancy)
              )
            }
            disabled={shouldDisableOptions}
          >
            <SelectTrigger id="target-veterancy" className="w-full h-7 text-xs py-0">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {targetVeterancyOptions.map((vet) => (
                <SelectItem key={vet} value={vet}>
                  {getVeterancyDisplayName(vet)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-0.5">
          <Label htmlFor="target-direction" className="text-[11px]">Direction</Label>
          <Select
            value={side}
            onValueChange={(value) => onSideChange(value as Side)}
            disabled={shouldDisableOptions}
          >
            <SelectTrigger id="target-direction" className="w-full h-7 text-xs py-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={Side.FRONT}>Front</SelectItem>
              <SelectItem value={Side.SIDE}>Side</SelectItem>
              <SelectItem value={Side.REAR}>Rear</SelectItem>
              <SelectItem value={Side.TOP}>Top</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-0.5">
          <Label htmlFor="target-terrain" className="text-[11px]">Terrain</Label>
          <Select
            value={terrain}
            onValueChange={onTerrainChange}
            disabled={shouldDisableOptions}
          >
            <SelectTrigger id="target-terrain" className="w-full h-7 text-xs py-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableTerrains.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        </div>
      </div>

      {/* Distance Control */}
      <div className="space-y-1.5">
        <div className="text-xs font-semibold flex items-center gap-1 text-muted-foreground">
          <Gauge className="h-3 w-3" />
          Distance
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="distance" className="text-[11px] font-medium whitespace-nowrap">
              Range
            </Label>
            <div className="flex items-center gap-1.5 flex-1">
              <Input
                id="distance-input"
                type="number"
                min={minRange}
                max={maxRange}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                disabled={shouldDisableOptions}
                className="h-7 w-20 text-xs"
                placeholder={`${minRange}-${maxRange}`}
              />
              <span className="text-[11px] text-muted-foreground">m</span>
            </div>
          </div>
          <div className="relative">
            <Slider
              id="distance"
              min={minRange}
              max={maxRange}
              value={[distance]}
              onValueChange={(value) => onDistanceChange(value[0] ?? distance)}
              disabled={shouldDisableOptions}
              step={1}
            />
          </div>
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>{minRange}m</span>
            <span>{maxRange}m</span>
          </div>
        </div>
      </div>
    </div>
  );
}

